import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { cld } from "@/lib/cld";
import { useSettings } from "@/contexts/SettingsContext";
import { Section, Overline, SectionTitle, GoldDivider } from "@/components/Primitives";
import Amenities from "@/components/Amenities";
import GalleryMarquee from "@/components/GalleryMarquee";
import { Star, ArrowRight, ChevronRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxmaW5lJTIwZGluaW5nJTIwcGxhdGVkJTIwZGlzaCUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3ODE4Mjk1OTR8MA&ixlib=rb-4.1.0&q=85";
const WINE_IMG = "https://images.unsplash.com/photo-1547595628-c61a29f496f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxzb21tZWxpZXIlMjBwb3VyaW5nJTIwd2luZSUyMGRhcmt8ZW58MHx8fHwxNzgxODI5NTk0fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, i18n } = useTranslation();
  const { settings: s } = useSettings();
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/menu").then(r => setMenu(r.data)).catch(() => {});
    api.get("/reviews").then(r => setReviews(r.data)).catch(() => {});
    api.get("/gallery").then(r => setGallery(r.data || [])).catch(() => {});
  }, []);

  const signatures = (menu.items || []).filter(it => (it.tags || []).includes("signature")).slice(0, 6);
  const lang = i18n.language;
  const tx = (obj) => obj?.[lang] || obj?.fr || "";

  // Real interior photos from the gallery — prefer dining room ("salle") first, then ambiance.
  const realPhotos = useMemo(() => {
    const salle = gallery.filter(g => g.type === "photo" && g.category === "salle");
    const ambi  = gallery.filter(g => g.type === "photo" && g.category === "ambiance");
    return [...salle, ...ambi];
  }, [gallery]);
  const ambianceImg = useMemo(() => realPhotos[0]?.url || null, [realPhotos]);
  const signatureSideImg = useMemo(() => realPhotos[1]?.url || realPhotos[0]?.url || null, [realPhotos]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[680px] w-full overflow-hidden" data-testid="hero">
        <img src={HERO_IMG} alt="Le 17 — plat signature" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-32">
          <div className="max-w-3xl fade-up">
            <Overline className="mb-5">{t("hero.overline")}</Overline>
            <h1 className="font-serif-luxe text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#F8F5EF] leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-lg text-[#F8F5EF]/85 max-w-xl leading-relaxed font-light">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/menu" data-testid="hero-cta-menu" className="btn-gold">{t("hero.cta_menu")} <ArrowRight size={14} /></Link>
              <Link to="/reservation" data-testid="hero-cta-reserve" className="btn-ghost-gold">{t("hero.cta_reserve")}</Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 right-6 lg:right-10 hidden lg:flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-[#F8F5EF]/60">
          <div className="w-px h-12 bg-[#D4AF37]/40" />
          <span>Scroll</span>
        </div>
      </section>

      {/* INTRO */}
      <Section testId="home-intro">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Overline>{t("home.intro_overline")}</Overline>
            <SectionTitle>{t("home.intro_title")}</SectionTitle>
            <p className="text-[#F8F5EF]/75 leading-relaxed text-base">{t("home.intro_text")}</p>
            <div className="mt-8">
              <Link to="/restaurant" className="text-[#D4AF37] tracking-[0.2em] uppercase text-xs hover:text-[#E5C869] inline-flex items-center gap-2" data-testid="home-discover-restaurant">
                {t("nav.restaurant")} <ChevronRight size={14} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden">
              {signatureSideImg && (
                <img src={cld(signatureSideImg, "wide")} alt="ambiance Le 17 Mulhouse" className="w-full h-[480px] object-cover transition-transform duration-700 hover:scale-105" />
              )}
              <div className="absolute inset-0 border border-[#D4AF37]/20" />
            </div>
          </div>
        </div>
      </Section>

      {/* SIGNATURES */}
      <Section testId="home-signatures" className="bg-[#0A0A0A]">
        <div className="text-center mb-16">
          <Overline>{t("home.signatures_overline")}</Overline>
          <SectionTitle className="!text-center">{t("home.signatures_title")}</SectionTitle>
          <GoldDivider />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {signatures.length > 0 ? signatures.map((it, idx) => (
            <div key={it.id} className="luxe-card overflow-hidden" data-testid={`signature-${idx}`}>
              <div className="p-8">
                <div className="overline mb-3">{tx(menu.categories.find(c => c.key === it.category)?.name)}</div>
                <h3 className="font-serif-luxe text-2xl text-[#F8F5EF] mb-3">{tx(it.name)}</h3>
                <p className="text-sm text-[#F8F5EF]/65 leading-relaxed min-h-[3rem]">{tx(it.description)}</p>
                <div className="mt-6 pt-4 border-t border-[#D4AF37]/15 flex items-center justify-between">
                  <span className="text-[#D4AF37] font-serif-luxe text-xl">{it.price.toFixed(2)} €</span>
                  <Link to="/menu" className="text-[10px] tracking-[0.3em] uppercase text-[#F8F5EF]/60 hover:text-[#D4AF37]">{t("blog_page.read_more")}</Link>
                </div>
              </div>
            </div>
          )) : (
            <div className="col-span-3 text-center text-[#F8F5EF]/50">—</div>
          )}
        </div>
        <div className="text-center mt-12">
          <Link to="/menu" className="btn-ghost-gold" data-testid="signatures-view-all">{t("hero.cta_menu")}</Link>
        </div>
      </Section>

      {/* AMBIANCE */}
      <Section testId="home-ambiance">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="relative overflow-hidden">
              {ambianceImg && (
                <img src={cld(ambianceImg, "wide")} alt="salle du restaurant Le 17 Mulhouse" className="w-full h-[520px] object-cover" />
              )}
              <div className="absolute inset-0 border border-[#D4AF37]/20" />
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Overline>{t("home.ambiance_overline")}</Overline>
            <SectionTitle>{t("home.ambiance_title")}</SectionTitle>
            <p className="text-[#F8F5EF]/75 leading-relaxed">{t("home.ambiance_text")}</p>
            <Link to="/gallery" className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] tracking-[0.2em] uppercase text-xs hover:text-[#E5C869]" data-testid="home-discover-gallery">
              {t("nav.gallery")} <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </Section>

      {/* AMENITIES */}
      <Section testId="home-amenities" className="bg-[#0A0A0A]">
        <div className="max-w-5xl">
          <Amenities />
        </div>
      </Section>

      {/* GALLERY MARQUEE */}
      <GalleryMarquee />

      {/* RESERVATION CTA */}
      <Section testId="home-reservation" className="bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={WINE_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative text-center max-w-2xl mx-auto">
          <Overline>{t("home.reservation_overline")}</Overline>
          <SectionTitle className="!text-center">{t("home.reservation_title")}</SectionTitle>
          <p className="text-[#F8F5EF]/75 mb-10">{t("home.reservation_text")}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/reservation" className="btn-gold" data-testid="home-reserve-btn">{t("hero.cta_reserve")}</Link>
            {s && <a href={`https://wa.me/${s.whatsapp_raw}`} target="_blank" rel="noreferrer" className="btn-ghost-gold" data-testid="home-whatsapp-btn">WhatsApp</a>}
          </div>
        </div>
      </Section>

      {/* REVIEWS */}
      <Section testId="home-reviews">
        <div className="text-center mb-12">
          <Overline>{t("home.reviews_overline")}</Overline>
          <SectionTitle className="!text-center">{t("home.reviews_title")}</SectionTitle>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.slice(0, 3).map((r, idx) => (
            <div key={r.id} className="luxe-card p-8" data-testid={`review-${idx}`}>
              <div className="flex gap-1 mb-4">
                {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={14} className="fill-[#D4AF37] text-[#D4AF37]" />)}
              </div>
              <p className="text-[#F8F5EF]/85 italic leading-relaxed font-serif-luxe text-lg">"{tx(r.comment)}"</p>
              <div className="mt-6 pt-4 border-t border-[#D4AF37]/15 flex items-center justify-between">
                <span className="text-sm text-[#F8F5EF]/80">{r.author}</span>
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">{r.source}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* MAP */}
      <Section testId="home-map" className="!py-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#D4AF37]/20">
          <div className="lg:col-span-5 p-12 bg-[#1A1A1A]">
            <Overline>{t("home.location_overline")}</Overline>
            <SectionTitle>{t("home.location_title")}</SectionTitle>
            <div className="space-y-3 text-sm text-[#F8F5EF]/80">
              <div>{s?.address}</div>
              <div>{s?.phone}</div>
              <div className="whitespace-pre-line">{s?.hours?.[lang] || s?.hours?.fr}</div>
            </div>
            {s?.maps_url && (
              <a href={s.maps_url} target="_blank" rel="noreferrer" className="mt-8 btn-ghost-gold inline-flex" data-testid="home-directions">
                {t("mobile_bar.directions")} <ArrowRight size={14} />
              </a>
            )}
          </div>
          <div className="lg:col-span-7 min-h-[400px]">
            {s?.maps_embed && (
              <iframe
                title="Le 17 map"
                src={s.maps_embed}
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full min-h-[400px] grayscale contrast-125"
              />
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
